import { alpha, Stack, Table, TableBody, TableCell, TableContainer, TableRow, tableRowClasses, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import {
  enqueueSnackbar,
  formatAmount,
  getFormattedDateString,
  ITableHeadCustomType,
  TableHeadCustom,
  TablePaginationCustom,
  TableSpinner,
  useTable,
  useUserDetailsStore,
} from '@zeta-core';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';

interface TableData {
  fundName: string;
  category: string;
  riskLevel: string;
  holding: string;
  growth: string;
}

const HEAD_LABEL: ITableHeadCustomType[] = [
  { label: 'Fund Name', width: 353, show: true, id: 'securityName' },
  { label: 'Category', width: 353, show: true, id: 'securityType' },
  { label: 'Valuation Date', width: 353, show: true, id: 'valuationDate' },
  { label: 'Value of Holding', width: 353, show: true, id: 'currentValue' },
  { label: 'Profit/Loss', width: 353, show: true, id: 'dailyChangePercentage' },
];

const FundBreakdownTable = ({ isClient }: { isClient?: boolean }) => {
  const userStore = useUserDetailsStore((state) => state.user);

  const location = useLocation();
  const state = location?.state;
  const clientID = isClient ? userStore?.accessibleEntityID : state?.details?.clientId;

  const [allocationData, setAllocationData] = useState();
  const theme = useTheme();
  const table = useTable({ defaultRowsPerPage: 5, defaultOrderBy: 'securityName' });

  const getAllocationData = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getFundAllocation(payload) : FinancialMisService().getFundAllocationAdvisor(payload),
    onSuccess: (response) => {
      setAllocationData(response?.data?.funds);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) {
      getAllocationData?.mutateAsync({
        clientID,
        accountId: state?.details?.accountId || state?.accountId,
        pageSize: table?.rowsPerPage,
        pageIndex: table?.page + 1,
        sort: table.orderBy,
        desc: table.order === 'desc',
      });
    }
  }, [table?.rowsPerPage, table?.page, clientID, table.order, table.orderBy]);

  return (
    <>
      <TableContainer sx={{ textOverflow: 'ellipsis' }}>
        <Table
          role="grid"
          stickyHeader
          size={table.dense ? 'small' : 'medium'}
          sx={{
            [`& .${tableRowClasses.root}`]: {
              '&:nth-of-type(even)': {
                backgroundColor: alpha(theme.palette.grey[500], 0.08),
              },
              '&:last-child td, &:last-child th': {
                border: 0,
              },
            },
          }}
        >
          <TableHeadCustom headLabel={HEAD_LABEL} order={table.order} orderBy={table.orderBy} onSort={table.onSort} />

          <TableBody>
            {getAllocationData?.isPending && <TableSpinner colSpan={12} />}
            {!allocationData?.length && !getAllocationData?.isPending && (
              <TableCell colSpan={12} sx={{ borderLeft: '3px solid #000000 !important' }}>
                <Stack alignItems="center" justifyContent="center" height="15vh">
                  <Typography variant="subtitle2" color="text.secondary">
                    No Fund available
                  </Typography>
                </Stack>
              </TableCell>
            )}
            {!getAllocationData?.isPending &&
              allocationData?.map((item, index) => {
                const difference = item?.dailyChange;
                const minusRemoved = Math.abs(item?.dailyChange);
                const minusRemovedPercentage = Math.abs(item?.dailyChangePercentage);
                return (
                  <TableRow key={index}>
                    <TableCell
                      sx={{ borderLeft: '3px solid #000000 !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}
                    >
                      <Tooltip title={item?.securityName ?? '--'}>{item?.securityName ?? '--'}</Tooltip>
                    </TableCell>
                    <TableCell>{item?.securityType ?? '--'}</TableCell>
                    <TableCell>{getFormattedDateString(item?.valuationDate, 'DD/MM/YYYY')}</TableCell>
                    <TableCell>{formatAmount(item?.holdingValue)}</TableCell>
                    <TableCell>
                      {difference ? (
                        <Stack direction="row" alignItems="center">
                          <Typography variant="subtitle2" sx={{ color: difference > 0 ? theme?.palette?.success?.main : theme?.palette?.error?.main }}>
                            {difference > 0 ? '+' : '-'} {formatAmount(minusRemoved)}
                            <Typography variant="chipLabel">({minusRemovedPercentage}%)</Typography>
                          </Typography>
                        </Stack>
                      ) : (
                        <>{minusRemovedPercentage}%</>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationCustom
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.onChangePage}
        onChangeDense={table.onChangeDense}
        count={getAllocationData?.data?.data?.totalRecords || 0}
        onRowsPerPageChange={table.onChangeRowsPerPage}
      />
    </>
  );
};

export default FundBreakdownTable;
